import React, { useEffect, useState } from "react";
import { api, CAD } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Download } from "lucide-react";
import { Button } from "../components/ui/button";

export default function B2CVarianceReport() {
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ group_by: "college" });

  const load = async () => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([_,v]) => v && v !== "all"));
    const r = await api.get(`/b2c/reports/variance?${qs}`); setData(r.data);
  };
  useEffect(() => { api.get("/b2c/meta").then(r=>setMeta(r.data)); }, []);
  useEffect(() => { load(); }, [filters]); // eslint-disable-line
  if (!meta || !data) return <div>Loading…</div>;

  const exportCsv = () => {
    const headers = ["Group","Claims","Expected (CAD)","Invoiced (CAD)","Received (CAD)","Δ Invoiced-Expected","Δ Received-Invoiced","Δ Received-Expected"];
    const rows = data.rows.map(r => [r.label, r.count, r.expected, r.invoiced, r.received, r.variance_invoiced_vs_expected, r.variance_received_vs_invoiced, r.variance_received_vs_expected]);
    const csv = [headers, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `variance-${filters.group_by}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const chip = (n) => n > 0 ? "text-emerald-700" : n < 0 ? "text-red-700" : "text-slate-600";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="heading-font text-3xl font-bold text-slate-900">Commission Variance Report</h1><p className="text-sm text-slate-500 mt-1">Expected vs Invoiced vs Received — grouped by College or Intake (CAD).</p></div>
        <Button variant="outline" onClick={exportCsv} data-testid="variance-export"><Download className="w-4 h-4 mr-2"/>Export CSV</Button>
      </div>
      <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded-xl border border-slate-200">
        <span className="text-xs uppercase text-slate-500 pl-1 font-medium">Group by</span>
        <Select value={filters.group_by} onValueChange={v=>setFilters({...filters, group_by:v})}>
          <SelectTrigger className="w-40 h-9 bg-white" data-testid="variance-groupby"><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value="college">College</SelectItem><SelectItem value="intake">Intake</SelectItem></SelectContent>
        </Select>
        <Select value={filters.college_id||"all"} onValueChange={v=>setFilters({...filters, college_id:v})}>
          <SelectTrigger className="w-56 h-9 bg-white"><SelectValue placeholder="College"/></SelectTrigger>
          <SelectContent><SelectItem value="all">All Colleges</SelectItem>{meta.colleges.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.intake_id||"all"} onValueChange={v=>setFilters({...filters, intake_id:v})}>
          <SelectTrigger className="w-44 h-9 bg-white"><SelectValue placeholder="Intake"/></SelectTrigger>
          <SelectContent><SelectItem value="all">All Intakes</SelectItem>{meta.intakes.map(i=><SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Claims" value={data.totals.count}/>
        <Kpi label="Expected" value={CAD(data.totals.expected)}/>
        <Kpi label="Invoiced" value={CAD(data.totals.invoiced)}/>
        <Kpi label="Received" value={CAD(data.totals.received)} accent/>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="text-left px-4 py-3">{filters.group_by === "intake" ? "Intake" : "College"}</th>
              <th className="text-right px-4 py-3">Claims</th>
              <th className="text-right px-4 py-3">Expected</th>
              <th className="text-right px-4 py-3">Invoiced</th>
              <th className="text-right px-4 py-3">Received</th>
              <th className="text-right px-4 py-3">Δ Inv−Exp</th>
              <th className="text-right px-4 py-3">Δ Rec−Inv</th>
              <th className="text-right px-4 py-3">Δ Rec−Exp</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r=>(
              <tr key={r.label} className="border-t border-slate-100" data-testid={`variance-row-${r.label}`}>
                <td className="px-4 py-3 font-medium">{r.label}</td>
                <td className="px-4 py-3 text-right">{r.count}</td>
                <td className="px-4 py-3 text-right mono">{CAD(r.expected)}</td>
                <td className="px-4 py-3 text-right mono">{CAD(r.invoiced)}</td>
                <td className="px-4 py-3 text-right mono font-semibold">{CAD(r.received)}</td>
                <td className={`px-4 py-3 text-right mono ${chip(r.variance_invoiced_vs_expected)}`}>{CAD(r.variance_invoiced_vs_expected)}</td>
                <td className={`px-4 py-3 text-right mono ${chip(r.variance_received_vs_invoiced)}`}>{CAD(r.variance_received_vs_invoiced)}</td>
                <td className={`px-4 py-3 text-right mono ${chip(r.variance_received_vs_expected)}`}>{CAD(r.variance_received_vs_expected)}</td>
              </tr>
            ))}
            {data.rows.length===0 && <tr><td colSpan={8} className="text-center py-10 text-slate-500">No commission data.</td></tr>}
          </tbody>
          {data.rows.length > 0 && (
            <tfoot className="bg-slate-50 font-semibold">
              <tr>
                <td className="px-4 py-3">TOTAL</td>
                <td className="px-4 py-3 text-right">{data.totals.count}</td>
                <td className="px-4 py-3 text-right mono">{CAD(data.totals.expected)}</td>
                <td className="px-4 py-3 text-right mono">{CAD(data.totals.invoiced)}</td>
                <td className="px-4 py-3 text-right mono">{CAD(data.totals.received)}</td>
                <td className={`px-4 py-3 text-right mono ${chip(data.totals.variance_invoiced_vs_expected)}`}>{CAD(data.totals.variance_invoiced_vs_expected)}</td>
                <td className={`px-4 py-3 text-right mono ${chip(data.totals.variance_received_vs_invoiced)}`}>{CAD(data.totals.variance_received_vs_invoiced)}</td>
                <td className={`px-4 py-3 text-right mono ${chip(data.totals.variance_received_vs_expected)}`}>{CAD(data.totals.variance_received_vs_expected)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
const Kpi = ({label, value, accent}) => (
  <div className={`bg-white rounded-xl border p-4 ${accent ? "border-orange-200" : "border-slate-200"}`}>
    <div className="text-xs uppercase text-slate-500">{label}</div>
    <div className="heading-font text-xl font-bold text-slate-900 mono mt-1">{value}</div>
  </div>
);
